import * as Dialog from "@radix-ui/react-dialog";
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CalendarClock, ClipboardPaste, Download, PackagePlus, QrCode, Plus, Search, Trash2, Truck, X, Check } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router";
import { toast } from "sonner";
import { PageHeader, usePermission, useTenant } from "@/components/AppShell";
import { Badge, Button, Card, cx, EmptyState, Input, Skeleton, ValidationBadge } from "@/components/ui";
import { api, ApiError, type InboundItem, type InboundStatus, type ProductItem } from "@/lib/api";
import { fmtNumber, fmtRelative } from "@/lib/format";
import { Pager } from "@/components/Pager";
import { intlLocale, t, msg } from "@/lib/i18n";

const STATUS: Record<InboundStatus, { label: string; tone: "neutral" | "info" | "warning" | "success" }> = {
  draft: { label: msg("Wird übermittelt …"), tone: "neutral" },
  announced: { label: msg("Angekündigt"), tone: "info" },
  partially_received: { label: msg("Teilweise eingegangen"), tone: "warning" },
  received: { label: msg("Eingegangen"), tone: "success" },
  closed: { label: msg("Abgeschlossen"), tone: "neutral" },
  cancelled: { label: msg("Storniert"), tone: "neutral" },
};

/** Status-Filter der Liste */
/** feste Spalten der Liste (Kopfzeile und Zeilen identisch) */
const INBOUND_COLS = "md:grid-cols-[minmax(0,1fr)_170px_200px_130px_150px]";

const FILTERS: { key: string; label: string; statuses?: InboundStatus[] }[] = [
  { key: "all", label: msg("Alle") },
  { key: "open", label: msg("Angekündigt"), statuses: ["draft", "announced"] },
  { key: "partial", label: msg("Teilweise eingegangen"), statuses: ["partially_received"] },
  { key: "done", label: msg("Eingegangen"), statuses: ["received", "closed"] },
];

/** Vorbelegung aus der URL (Handlungsbedarf, Nachbestell-Assistent): skus=SKU:Menge,SKU2:Menge */
export const parsePrefill = (v: string | null) =>
  (v ?? "").split(",").map((x) => x.trim()).filter(Boolean).slice(0, 200).map((x) => {
    const i = x.lastIndexOf(":");
    const qty = i > 0 ? Math.round(Number(x.slice(i + 1))) : NaN;
    return { sku: i > 0 && qty > 0 ? x.slice(0, i) : x, quantity: qty > 0 ? qty : 1 };
  });

export function InboundsPage() {
  const { tenantId } = useTenant();
  const mayEdit = usePermission()("inbounds");
  const [params, setParams] = useSearchParams();
  const [open, setOpen] = useState(params.get("new") === "1");
  const [prefill] = useState(() => (params.get("new") === "1" ? parsePrefill(params.get("skus")) : []));
  // Einmal-Parameter nach dem Öffnen entfernen (Neuladen öffnet den Dialog nicht erneut)
  useEffect(() => {
    if (params.has("new") || params.has("skus")) setParams((p) => { const n = new URLSearchParams(p); n.delete("new"); n.delete("skus"); return n; }, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [expanded, setExpanded] = useState<string | null>(null);
  // nach dem Anlegen: Zeile öffnen und auf Wunsch direkt die Inbound Note
  const [noteFor, setNoteFor] = useState<string | null>(null);
  // Suche (Nummer, Lieferant, Bestellnr., SKU, Artikel, Charge) – kurz entprellt
  const [search, setSearch] = useState(params.get("q") ?? "");
  const [term, setTerm] = useState(params.get("q") ?? "");
  useEffect(() => {
    const id = setTimeout(() => setTerm(search.trim()), 250);
    return () => clearTimeout(id);
  }, [search]);
  const [filter, setFilter] = useState<string>(() => (params.get("status") === "announced" ? "open" : "all"));
  const statusParam = FILTERS.find((f) => f.key === filter)?.statuses?.join(",");
  const [page, setPage] = useState(1);
  useEffect(() => setPage(1), [term, statusParam]);
  const paged = useQuery({
    queryKey: ["inbounds", tenantId, "page", term, statusParam, page],
    queryFn: () => api.inbounds.page(tenantId, { ...(term ? { q: term } : {}), ...(statusParam ? { status: statusParam } : {}), page, pageSize: 100 }),
    placeholderData: keepPreviousData,
    // live: frisch angelegte (Entwurf → wird übermittelt) alle 3 s, sonst alle 30 s (Eingangsfortschritt aus dem Lager)
    refetchInterval: (qq) => (qq.state.data?.items.some((i) => i.status === "draft") ? 3000 : 30_000),
  });
  const q = { ...paged, data: paged.data?.items };

  return (
    <div className="pb-16">
      <PageHeader
        title={t("Wareneingänge")}
        description={t("Melde Lieferungen an, bevor sie bei uns ankommen – so wird die Ware schneller eingebucht und ist früher verkaufbar.")}
        actions={mayEdit ? <Button variant="primary" icon={<Plus className="size-4" />} onClick={() => setOpen(true)}>{t("Wareneingang anmelden")}</Button> : undefined}
      />
      <div className="px-4 md:px-8">
        {/* Suche groß über die volle Breite, Filter darunter (wie Aufträge, Artikel, Retouren) */}
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={t("Suchen: Nummer, Lieferant, Bestellnr., SKU, Artikel, Charge …")}
          leading={<Search className="size-4" />}
          suffix={search ? <button onClick={() => setSearch("")} className="size-6 grid place-items-center rounded-md text-fg-subtle hover:bg-surface-2 hover:text-fg" aria-label={t("Suche leeren")}><X className="size-3.5" /></button> : undefined}
          className="h-11 text-[14px]"
          aria-label={t("Wareneingänge durchsuchen")}
        />
        <div className="mt-3 mb-3 flex flex-wrap items-center gap-2">
          <div className="inline-flex flex-wrap p-0.5 rounded-lg bg-surface-2 border border-border">
            {FILTERS.map((f) => (
              <button key={f.key} onClick={() => setFilter(f.key)} aria-pressed={filter === f.key} className={cx("h-7 px-3 rounded-md text-[12.5px] font-medium", filter === f.key ? "bg-surface shadow-sm text-fg" : "text-fg-muted hover:text-fg")}>
                {t(f.label)}
              </button>
            ))}
          </div>
        </div>
        <Card className="overflow-hidden">
          {/* feste Spalten: Wareneingang · Status · Fortschritt · Abweichung · Datum */}
          {!!q.data?.length && (
            <div className={cx("hidden md:grid gap-x-4 px-4 h-9 items-center border-b border-border bg-surface-2/50 text-[12px] font-medium text-fg-subtle", INBOUND_COLS)}>
              <span>{t("Wareneingang")}</span><span>{t("Status")}</span><span>{t("Eingebucht")}</span><span>{t("Abweichung")}</span><span className="text-right">{t("Datum")}</span>
            </div>
          )}
          {q.isLoading && <div className="p-4 space-y-3">{[0, 1, 2].map((i) => <Skeleton key={i} className="h-10 w-full" />)}</div>}
          <ul className="divide-y divide-border">
            {q.data?.map((i) => <InboundRow key={i.id} i={i} open={expanded === i.id} onToggle={() => setExpanded(expanded === i.id ? null : i.id)} noteRequested={noteFor === i.id} onNoteHandled={() => setNoteFor(null)} />)}
          </ul>
          {paged.data && <Pager total={paged.data.total} page={page} pageSize={paged.data.pageSize} onPage={(n) => { setPage(n); window.scrollTo({ top: 0, behavior: "smooth" }); }} />}
          {q.data?.length === 0 && (term || filter !== "all") && (
            <EmptyState icon={<Search className="size-5" />} title={t("Nichts gefunden")} action={<Button onClick={() => { setSearch(""); setFilter("all"); }}>{t("Suche zurücksetzen")}</Button>}>
              {t("Kein Wareneingang passt")}{term ? <> {t("zu „")}{term}“</> : null}.
            </EmptyState>
          )}
          {q.data?.length === 0 && !term && filter === "all" && (
            <EmptyState icon={<Truck className="size-5" />} title={t("Noch keine Wareneingänge")} action={<Button variant="primary" onClick={() => setOpen(true)}>{t("Ersten Wareneingang anmelden")}</Button>}>
              {t("Ein Avis sagt dem Lager, was wann ankommt. Du kannst Positionen auch direkt aus Excel einfügen.")}
            </EmptyState>
          )}
        </Card>
      </div>
      <NewInboundDialog open={open} onOpenChange={setOpen} prefill={prefill} onCreated={(id) => setExpanded(id)} onNote={(id) => { setExpanded(id); setNoteFor(id); }} />
    </div>
  );
}

/** Inbound Note: Etikett (A6) oder Begleitschein (A4) je Packstück, QR-Code mit der Anlieferungsnummer */
function InboundNoteDialog({ inbound, open, onOpenChange }: { inbound: InboundItem; open: boolean; onOpenChange: (o: boolean) => void }) {
  const { tenant } = useTenant();
  const [format, setFormat] = useState<"a6" | "a4">("a6");
  const [packages, setPackages] = useState("1");
  const [busy, setBusy] = useState(false);
  const n = Math.max(1, Math.min(999, Math.floor(Number(packages) || 1)));
  const download = async () => {
    setBusy(true);
    try {
      const { downloadInboundNote } = await import("@/lib/inbound-note");
      await downloadInboundNote({ inbound, merchant: tenant.name, packages: n, format });
      toast.success(`Inbound Note erstellt (${n} ${n === 1 ? "Seite" : "Seiten"}, ${format.toUpperCase()})`);
      onOpenChange(false);
    } catch {
      toast.error("PDF konnte nicht erstellt werden");
    } finally {
      setBusy(false);
    }
  };
  const options = [
    { value: "a6" as const, title: msg("A6 – Etikett"), text: msg("Zum Aufkleben auf jedes Packstück (z. B. Versandetikett-Drucker).") },
    { value: "a4" as const, title: msg("A4 – Begleitschein"), text: msg("Zum Beilegen oder Aufkleben, mit vollständiger Artikelliste.") },
  ];
  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-black/30 animate-fade-in" />
        <Dialog.Content className="fixed z-50 left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[calc(100vw-32px)] max-w-md rounded-2xl bg-surface border border-border shadow-2xl p-5 animate-pop-in">
          <Dialog.Title className="text-[16px] font-semibold flex items-center gap-2"><QrCode className="size-4 text-fg-subtle" /> {t("Inbound Note für")} {inbound.inboundNumber}</Dialog.Title>
          <Dialog.Description className="text-[12.5px] text-fg-muted mt-1">{t("Zum Weitergeben an den Lieferanten oder zum Beilegen. Eine Seite je Packstück – gleicher QR-Code mit der Anlieferungsnummer, dazu „1/3“, „2/3“ … (auf Englisch).")}</Dialog.Description>
          <div className="mt-4 grid gap-2">
            {options.map((o) => (
              <label key={o.value} className={cx("flex items-start gap-2.5 rounded-xl border p-3 cursor-pointer", format === o.value ? "border-ink bg-surface-2" : "border-border hover:bg-surface-2/60")}>
                <input type="radio" className="mt-1 accent-ink" checked={format === o.value} onChange={() => setFormat(o.value)} />
                <span><span className="block text-[13px] font-medium">{t(o.title)}</span><span className="block text-[12px] text-fg-muted">{t(o.text)}</span></span>
              </label>
            ))}
          </div>
          <label className="mt-4 flex items-center justify-between gap-3 text-[13px]">
            <span className="font-medium">{t("Anzahl Packstücke")}</span>
            <input value={packages} onChange={(e) => setPackages(e.target.value.replace(/[^0-9]/g, "").slice(0, 3))} inputMode="numeric" className="w-20 h-9 rounded-lg border border-border bg-surface px-2.5 text-right tabular-nums" aria-label={t("Anzahl Packstücke")} />
          </label>
          <div className="mt-5 flex justify-end gap-2">
            <Dialog.Close asChild><Button variant="ghost">{t("Abbrechen")}</Button></Dialog.Close>
            <Button variant="primary" icon={<Download className="size-4" />} loading={busy} onClick={() => void download()}>{t("PDF herunterladen (")}{n} {n === 1 ? t("Seite") : t("Seiten")})</Button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

function InboundRow({ i, open, onToggle, noteRequested, onNoteHandled }: { i: InboundItem; open: boolean; onToggle: () => void; noteRequested?: boolean; onNoteHandled?: () => void }) {
  const [noteOpen, setNoteOpen] = useState(false);
  useEffect(() => {
    if (noteRequested) { setNoteOpen(true); onNoteHandled?.(); }
  }, [noteRequested, onNoteHandled]);
  const expected = i.lines.reduce((n, l) => n + l.quantityExpected, 0);
  const received = i.lines.reduce((n, l) => n + l.quantityReceived, 0);
  const pct = expected ? Math.min(100, Math.round((received / expected) * 100)) : 0;
  const diffLines = i.lines.filter((l) => (i.status === "received" || i.status === "closed") && l.quantityReceived !== l.quantityExpected);
  return (
    <li>
      <button onClick={onToggle} aria-expanded={open} className={cx("w-full text-left grid grid-cols-[minmax(0,1fr)_auto] md:grid items-center gap-x-4 gap-y-1.5 px-4 py-3 hover:bg-surface-2/50", INBOUND_COLS)}>
        <div className="min-w-0">
          <div className="font-medium tabular-nums truncate">{i.inboundNumber}</div>
          <div className="text-[12px] text-fg-muted truncate">{i.supplier ?? t("Ohne Lieferant")}{i.purchaseOrderNumber ? ` · ${i.purchaseOrderNumber}` : ""}{i.warehouseName ? ` · ${i.warehouseName}` : ""}</div>
        </div>
        <div><Badge tone={STATUS[i.status].tone}>{t(STATUS[i.status].label)}</Badge></div>
        <div className="flex items-center gap-2">
          <div className="h-1.5 w-24 rounded-full bg-surface-2 overflow-hidden shrink-0">
            <div className={cx("h-full rounded-full", pct >= 100 ? "bg-success" : pct > 0 ? "bg-warning" : "bg-border-strong")} style={{ width: `${pct}%` }} />
          </div>
          <span className="text-[12px] text-fg-muted tabular-nums whitespace-nowrap">{fmtNumber(received)} / {fmtNumber(expected)}</span>
        </div>
        <div className="hidden md:block">{diffLines.length > 0 ? <Badge tone="warning">{diffLines.length} {t("Abweichung")}{diffLines.length > 1 ? t("en") : ""}</Badge> : <span className="text-fg-subtle">–</span>}</div>
        <span className="md:text-right text-[12px] text-fg-muted whitespace-nowrap">
          {i.appointment ? (
            <span className="inline-flex items-center gap-1 text-info font-medium"><CalendarClock className="size-3.5" /> {new Date(`${i.appointment.date}T12:00:00Z`).toLocaleDateString(intlLocale(), { day: "2-digit", month: "2-digit" })} · {i.appointment.from}{i.appointment.to ? `–${i.appointment.to}` : ""} {t("Uhr")}</span>
          ) : i.expectedAt ? (new Date(i.expectedAt) > new Date() ? t("erwartet {v}", { v: fmtRelative(i.expectedAt) }) : t("geplant {v}", { v: new Date(i.expectedAt).toLocaleDateString(intlLocale()) })) : fmtRelative(i.createdAt)}
        </span>
      </button>
      {open && (
        <div className="px-4 pb-4 animate-fade-in">
          {i.status !== "cancelled" && (
            <div className="mb-2 flex justify-end">
              <Button size="sm" icon={<QrCode className="size-3.5" />} onClick={() => setNoteOpen(true)}>{t("Inbound Note (PDF)")}</Button>
            </div>
          )}
          <InboundNoteDialog inbound={i} open={noteOpen} onOpenChange={setNoteOpen} />
          <AppointmentBox inbound={i} />
          <table className="w-full text-[13px]">
            <thead className="text-[12px] text-fg-subtle">
              <tr>
                <th className="text-left font-medium py-1.5">{t("Artikel")}</th>
                <th className="text-left font-medium px-3 w-40">{t("SKU")}</th>
                <th className="text-right font-medium px-3 w-28">{t("Angemeldet")}</th>
                <th className="text-right font-medium px-3 w-28">{t("Eingegangen")}</th>
                <th className="text-right font-medium pr-1 w-36">{t("Abweichung")}</th>
              </tr>
            </thead>
            <tbody>
              {i.lines.map((l) => {
                const d = l.quantityReceived - l.quantityExpected;
                const done = i.status === "received" || i.status === "closed";
                return (
                  <tr key={l.id} className="border-t border-border">
                    <td className="py-2">
                      <span className="font-medium">{l.name}</span>
                      {(l.batch || l.bestBefore) && (
                        <span className="block text-[11.5px] text-fg-muted">
                          {[l.batch && `Charge ${l.batch}`, l.bestBefore && `MHD ${new Date(l.bestBefore).toLocaleDateString(intlLocale())}`].filter(Boolean).join(" · ")}
                        </span>
                      )}
                    </td>
                    <td className="px-3 font-mono text-[12px] text-fg-muted whitespace-nowrap">{l.sku}</td>
                    <td className="px-3 text-right tabular-nums">{fmtNumber(l.quantityExpected)}</td>
                    <td className="px-3 text-right tabular-nums">{fmtNumber(l.quantityReceived)}</td>
                    <td className="text-right pr-1 whitespace-nowrap text-[12.5px]">
                      {d === 0 ? (
                        <span className="inline-flex items-center gap-1 text-success"><Check className="size-3.5" /> {done ? t("keine") : t("vollständig")}</span>
                      ) : d > 0 ? (
                        <span className="text-warning tabular-nums">{t("{n} mehr", { n: fmtNumber(d) })}</span>
                      ) : done ? (
                        <span className="text-danger tabular-nums">{t("{n} fehlen", { n: fmtNumber(-d) })}</span>
                      ) : (
                        <span className="text-fg-muted tabular-nums">{t("{n} noch offen", { n: fmtNumber(-d) })}</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </li>
  );
}

/** Fester Anliefertermin (z. B. Container) – erscheint im Kalender auf der Übersicht */
function AppointmentBox({ inbound }: { inbound: InboundItem }) {
  const { tenantId } = useTenant();
  const mayEdit = usePermission()("inbounds", "edit");
  const qc = useQueryClient();
  const a = inbound.appointment ?? null;
  const [edit, setEdit] = useState(false);
  const [v, setV] = useState({ date: "", from: "", to: "", note: "" });
  const open = () => { setV({ date: a?.date ?? inbound.expectedAt?.slice(0, 10) ?? "", from: a?.from ?? "", to: a?.to ?? "", note: a?.note ?? "" }); setEdit(true); };
  const save = useMutation({
    mutationFn: (clear: boolean) => api.inbounds.setAppointment(tenantId, inbound.id, clear ? null : { date: v.date, from: v.from, to: v.to || null, note: v.note.trim() || null }),
    onSuccess: (_r, clear) => {
      toast.success(clear ? "Termin entfernt" : "Anliefertermin gespeichert", { description: clear ? undefined : "Steht jetzt im Kalender auf der Übersicht." });
      setEdit(false);
      void qc.invalidateQueries({ queryKey: ["inbounds", tenantId] });
      void qc.invalidateQueries({ queryKey: ["calendar", tenantId] });
    },
    onError: (e) => toast.error("Nicht gespeichert", { description: e instanceof ApiError ? ((e.body as { message?: string })?.message ?? undefined) : undefined }),
  });
  // eingegangene Ware braucht keinen Termin mehr – ein eingetragener bleibt zur Info sichtbar
  const closed = inbound.status === "cancelled" || inbound.status === "closed" || inbound.status === "received";
  if (!a && (closed || !mayEdit)) return null;
  return (
    <div className="mb-3 rounded-xl border border-border bg-surface-2/40 px-3 py-2.5 text-[13px]">
      {!edit ? (
        <div className="flex flex-wrap items-center gap-2">
          <CalendarClock className="size-4 text-fg-subtle" />
          {a ? (
            <span><b className="font-medium">{t("Anliefertermin")} {new Date(`${a.date}T12:00:00Z`).toLocaleDateString(intlLocale(), { weekday: "short", day: "2-digit", month: "2-digit", year: "numeric" })}, {a.from}{a.to ? `–${a.to}` : ""} {t("Uhr")}</b>{a.note && <span className="text-fg-muted"> · {a.note}</span>}</span>
          ) : <span className="text-fg-muted">{t("Kein fester Anliefertermin – z. B. bei Containern Datum und Uhrzeit eintragen.")}</span>}
          {mayEdit && !closed && <Button size="sm" variant="ghost" className="ml-auto" onClick={open}>{a ? t("Ändern") : t("Termin eintragen")}</Button>}
        </div>
      ) : (
        <div className="space-y-2">
          <div className="grid grid-cols-2 sm:grid-cols-[150px_100px_100px_1fr] gap-2">
            <label className="block"><span className="text-[11.5px] font-medium text-fg-muted">{t("Datum")}</span><Input type="date" value={v.date} onChange={(e) => setV({ ...v, date: e.target.value })} className="mt-1" /></label>
            <label className="block"><span className="text-[11.5px] font-medium text-fg-muted">{t("von")}</span><Input type="time" value={v.from} onChange={(e) => setV({ ...v, from: e.target.value })} className="mt-1" /></label>
            <label className="block"><span className="text-[11.5px] font-medium text-fg-muted">{t("bis (optional)")}</span><Input type="time" value={v.to} onChange={(e) => setV({ ...v, to: e.target.value })} className="mt-1" /></label>
            <label className="block col-span-2 sm:col-span-1"><span className="text-[11.5px] font-medium text-fg-muted">{t("Hinweis (optional)")}</span><Input value={v.note} maxLength={300} placeholder={t("z. B. 40-Fuß-Container, Rampe 2")} onChange={(e) => setV({ ...v, note: e.target.value })} className="mt-1" /></label>
          </div>
          <div className="flex flex-wrap gap-2">
            {a && <Button size="sm" variant="ghost" loading={save.isPending && save.variables === true} onClick={() => save.mutate(true)}>{t("Termin entfernen")}</Button>}
            <Button size="sm" className="ml-auto" onClick={() => setEdit(false)}>{t("Abbrechen")}</Button>
            <Button size="sm" variant="primary" disabled={!v.date || !v.from} loading={save.isPending && save.variables === false} onClick={() => save.mutate(false)}>{t("Speichern")}</Button>
          </div>
        </div>
      )}
    </div>
  );
}

interface Line {
  /** eigene Kennung je Position – ein Artikel kann mit mehreren Chargen mehrfach vorkommen */
  key: string;
  product: Pick<ProductItem, "id" | "sku" | "name" | "validation" | "handling">;
  quantity: number;
  batch: string;
  bestBefore: string;
}

const lotManaged = (p: Line["product"]) => !!(p.handling?.batchManaged || p.handling?.bestBeforeManaged);
const newKey = () => Math.random().toString(36).slice(2, 10);

function NewInboundDialog({ open, onOpenChange, onCreated, onNote, prefill = [] }: { open: boolean; onOpenChange: (o: boolean) => void; onCreated?: (id: string) => void; onNote?: (id: string) => void; prefill?: { sku: string; quantity: number }[] }) {
  const { tenantId } = useTenant();
  const qc = useQueryClient();
  const [lines, setLines] = useState<Line[]>([]);
  const [supplier, setSupplier] = useState("");
  const [po, setPo] = useState("");
  const [expectedAt, setExpectedAt] = useState("");
  // Ziel-Lager: nur bei mehreren aktiven Lagern zur Auswahl, sonst Hauptlager
  const whq = useQuery({ queryKey: ["shipping-methods", tenantId], queryFn: () => api.backend.shippingMethods(tenantId), enabled: open, staleTime: 5 * 60_000, retry: false });
  const activeWarehouses = (whq.data?.warehouses ?? []).filter((w) => w.active || w.primary).sort((a, b) => Number(!!b.primary) - Number(!!a.primary) || (a.priority ?? 99) - (b.priority ?? 99));
  const [warehouseRef, setWarehouseRef] = useState("");
  const targetWarehouse = warehouseRef || whq.data?.activeWarehouseId || "";
  const [search, setSearch] = useState("");
  const [paste, setPaste] = useState<string | null>(null);
  const [unknown, setUnknown] = useState<string[]>([]);

  const results = useQuery({
    queryKey: ["inbound-search", tenantId, search],
    queryFn: () => api.products.list(tenantId, { search, pageSize: 8, status: "active" }),
    enabled: search.trim().length >= 2,
  });

  const add = (p: Line["product"], quantity = 1) =>
    setLines((ls) => {
      const i = ls.findIndex((l) => l.product.id === p.id);
      if (i < 0) return [...ls, { key: newKey(), product: p, quantity, batch: "", bestBefore: "" }];
      return ls.map((l, j) => (j === i ? { ...l, quantity: l.quantity + quantity } : l));
    });
  const update = (key: string, patch: Partial<Line>) => setLines((ls) => ls.map((l) => (l.key === key ? { ...l, ...patch } : l)));
  /** weitere Charge desselben Artikels direkt darunter */
  const splitLot = (key: string) =>
    setLines((ls) => {
      const i = ls.findIndex((l) => l.key === key);
      const next = { key: newKey(), product: ls[i]!.product, quantity: 1, batch: "", bestBefore: "" };
      return [...ls.slice(0, i + 1), next, ...ls.slice(i + 1)];
    });

  /** Excel/CSV: je Zeile "SKU <Tab|;|,> Menge" */
  const importPaste = async () => {
    const rows = (paste ?? "").split(/\r?\n/).map((r) => r.split(/\t|;|,/).map((c) => c.trim())).filter((r) => r[0]);
    const notFound: string[] = [];
    for (const [sku, qty] of rows) {
      const n = Number((qty ?? "1").replace(/\./g, "").replace(",", "."));
      const r = await api.products.list(tenantId, { search: sku!, pageSize: 5 });
      const p = r.items.find((x) => x.sku.toLowerCase() === sku!.toLowerCase());
      if (p && n > 0) add(p, Math.round(n));
      else notFound.push(sku!);
    }
    setUnknown(notFound);
    setPaste(null);
    const ok = rows.length - notFound.length;
    toast.success(`${ok} ${ok === 1 ? "Position" : "Positionen"} übernommen`, notFound.length ? { description: `${notFound.length} SKU nicht gefunden` } : undefined);
  };

  // Vorbelegung (z. B. fehlende Ware aus „Handlungsbedarf“) einmalig übernehmen
  // Ref statt State: wirkt sofort (auch wenn React den Effekt doppelt ausführt) – nie doppelt übernehmen
  const prefilled = useRef(false);
  useEffect(() => {
    if (!open || prefilled.current || !prefill.length) return;
    prefilled.current = true;
    void (async () => {
      const notFound: string[] = [];
      for (const { sku, quantity } of prefill) {
        const r = await api.products.list(tenantId, { search: sku, pageSize: 5 });
        const p = r.items.find((x) => x.sku.toLowerCase() === sku.toLowerCase());
        if (p) add(p, quantity);
        else notFound.push(sku);
      }
      setUnknown(notFound);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, prefill]);

  const blocked = lines.filter((l) => l.product.validation.status === "invalid");
  const total = lines.reduce((n, l) => n + l.quantity, 0);

  const create = useMutation({
    mutationFn: () =>
      api.inbounds.create(tenantId, {
        ...(supplier ? { supplier } : {}),
        ...(po ? { purchaseOrderNumber: po } : {}),
        ...(expectedAt ? { expectedAt: new Date(expectedAt).toISOString() } : {}),
        ...(activeWarehouses.length > 1 && targetWarehouse ? { warehouseRef: targetWarehouse } : {}),
        lines: lines.map((l) => ({ productId: l.product.id, quantity: l.quantity, ...(l.batch.trim() ? { batch: l.batch.trim() } : {}), ...(l.bestBefore ? { bestBefore: l.bestBefore } : {}) })),
      }),
    onSuccess: (r) => {
      toast.success(`${r.inboundNumber} ans Lager übermittelt`, { description: `${lines.length} ${lines.length === 1 ? "Position" : "Positionen"}, ${fmtNumber(total)} ${total === 1 ? "Einheit" : "Einheiten"}`, action: { label: msg("Inbound Note"), onClick: () => onNote?.(r.id) }, duration: 10_000 });
      onCreated?.(r.id);
      void qc.invalidateQueries({ queryKey: ["inbounds", tenantId] });
      setLines([]); setSupplier(""); setPo(""); setExpectedAt(""); setUnknown([]); setWarehouseRef("");
      onOpenChange(false);
    },
    onError: (e) => {
      const body = e instanceof ApiError ? (e.body as { error?: string; skus?: string[] }) : undefined;
      toast.error(body?.error === "products_blocked" ? `Stammdaten unvollständig: ${body.skus?.join(", ")}` : "Anlegen fehlgeschlagen");
    },
  });

  const minDate = useMemo(() => new Date().toISOString().slice(0, 10), []);

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-black/30 animate-fade-in" />
        <Dialog.Content aria-describedby={undefined} className="fixed z-50 inset-x-4 top-[6vh] mx-auto max-w-2xl max-h-[88vh] flex flex-col rounded-2xl border border-border bg-surface shadow-2xl animate-pop-in">
          <div className="px-6 pt-5 pb-4 border-b border-border flex items-start">
            <div>
              <Dialog.Title className="text-[17px] font-semibold">{t("Wareneingang anmelden")}</Dialog.Title>
              <p className="text-fg-muted mt-0.5">{t("Was kommt wann? Das Lager plant Personal und Platz danach.")}</p>
            </div>
            <Dialog.Close className="ml-auto size-8 grid place-items-center rounded-lg hover:bg-surface-2 text-fg-muted" aria-label={t("Schließen")}><X className="size-4" /></Dialog.Close>
          </div>

          <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
            {activeWarehouses.length > 1 && (
              <div>
                <span className="text-[12px] font-medium text-fg-muted">{t("Ziel-Lager")}</span>
                <div className="mt-1 grid sm:grid-cols-3 gap-2">
                  {activeWarehouses.map((w) => (
                    <button
                      key={w.warehouseId}
                      type="button"
                      onClick={() => setWarehouseRef(w.warehouseId)}
                      aria-pressed={targetWarehouse === w.warehouseId}
                      className={cx("rounded-lg border px-3 py-2 text-left", targetWarehouse === w.warehouseId ? "border-ink bg-surface-2" : "border-border hover:bg-surface-2/60")}
                    >
                      <span className="block text-[13px] font-medium">{w.name}{w.primary && <span className="ml-1.5 text-[11px] font-normal text-fg-subtle">{t("Hauptlager")}</span>}</span>
                      <span className="block text-[11.5px] text-fg-muted">{[w.city, w.country].filter(Boolean).join(", ")}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}
            <div className="grid sm:grid-cols-3 gap-3">
              <label className="block"><span className="text-[12px] font-medium text-fg-muted">{t("Lieferant")}</span><Input value={supplier} onChange={(e) => setSupplier(e.target.value)} placeholder={t("z. B. Müller GmbH")} className="mt-1" /></label>
              <label className="block"><span className="text-[12px] font-medium text-fg-muted">{t("Bestellnummer")}</span><Input value={po} onChange={(e) => setPo(e.target.value)} placeholder={t("optional")} className="mt-1" /></label>
              <label className="block"><span className="text-[12px] font-medium text-fg-muted">{t("Ankunft geplant")}</span><Input type="date" min={minDate} value={expectedAt} onChange={(e) => setExpectedAt(e.target.value)} className="mt-1" /></label>
            </div>

            <div>
              <div className="flex items-center mb-2">
                <span className="text-[12px] font-semibold text-fg-muted uppercase tracking-wide">{t("Positionen")}</span>
                <button onClick={() => setPaste(paste === null ? "" : null)} className="ml-auto inline-flex items-center gap-1.5 text-[12.5px] font-medium text-fg-muted hover:text-fg">
                  <ClipboardPaste className="size-3.5" /> {t("Aus Excel einfügen")}
                </button>
              </div>

              {paste !== null && (
                <div className="mb-3 rounded-xl border border-border bg-surface-2/50 p-3 animate-fade-in">
                  <textarea autoFocus value={paste} onChange={(e) => setPaste(e.target.value)} rows={5} placeholder={t("NL-SHP-001\t120\nNL-SOAP-009\t48")} className="w-full rounded-lg border border-border bg-surface p-2.5 font-mono text-[12.5px] outline-none focus:border-fg-muted" />
                  <div className="mt-2 flex items-center gap-2">
                    <span className="text-[12px] text-fg-subtle">{t("Spalten SKU und Menge aus Excel kopieren und hier einfügen.")}</span>
                    <Button size="sm" variant="primary" className="ml-auto" disabled={!paste.trim()} onClick={() => void importPaste()}>{t("Übernehmen")}</Button>
                  </div>
                </div>
              )}

              <div className="relative">
                <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder={t("Artikel suchen (SKU, Name, EAN) …")} leading={<Search className="size-3.5" />} />
                {results.data && search.trim().length >= 2 && (
                  <div className="absolute z-10 mt-1 w-full rounded-xl border border-border bg-surface shadow-xl p-1 animate-pop-in">
                    {results.data.items.length === 0 && <div className="px-3 py-2 text-fg-muted">{t("Kein Artikel gefunden")}</div>}
                    {results.data.items.map((p) => (
                      <button key={p.id} onClick={() => { add(p); setSearch(""); }} className="w-full flex items-center gap-2 h-9 px-2.5 rounded-lg hover:bg-surface-2 text-left">
                        <span className="font-mono text-[12px] text-fg-muted w-32 truncate">{p.sku}</span>
                        <span className="truncate flex-1">{p.name}</span>
                        <ValidationBadge status={p.validation.status} />
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {unknown.length > 0 && (
                <p className="mt-2 text-[12.5px] text-danger">{t("Nicht gefunden:")} {unknown.join(", ")} {t("– bitte SKU prüfen oder Artikel zuerst im Shop anlegen.")}</p>
              )}

              {lines.length > 0 ? (
                <ul className="mt-3 rounded-xl border border-border divide-y divide-border">
                  {lines.map((l) => (
                    <li key={l.key} className="px-3 py-2">
                    <div className="flex items-center gap-3">
                      <div className="min-w-0 flex-1">
                        <div className="truncate font-medium text-[13px]">{l.product.name}</div>
                        <div className="font-mono text-[11.5px] text-fg-muted flex items-center gap-2">{l.product.sku}{" "}
                          {l.product.validation.status === "invalid" && (
                            <span className="font-sans text-danger">
                              {t("Stammdaten unvollständig ·")}{" "}
                              {/* neuer Tab: das begonnene Avis bleibt erhalten */}
                              <a href={`/products?open=${l.product.id}`} target="_blank" rel="noreferrer" className="font-medium underline underline-offset-2">{t("ergänzen →")}</a>
                            </span>
                          )}</div>
                      </div>
                      <Input
                        type="number"
                        min={1}
                        value={String(l.quantity)}
                        onChange={(e) => update(l.key, { quantity: Math.max(1, Math.round(Number(e.target.value) || 1)) })}
                        className="w-24 [&_input]:text-right"
                        aria-label={t("Menge {sku}", { sku: l.product.sku })}
                      />
                      <button onClick={() => setLines((ls) => ls.filter((x) => x.key !== l.key))} className="size-8 grid place-items-center rounded-lg text-fg-subtle hover:text-danger hover:bg-surface-2" aria-label={t("Position entfernen")}>
                        <Trash2 className="size-4" />
                      </button>
                    </div>
                    {lotManaged(l.product) && (
                      <div className="mt-2 flex flex-wrap items-end gap-2 pl-0.5">
                        {l.product.handling?.batchManaged && (
                          <label className="block">
                            <span className="text-[11.5px] font-medium text-fg-muted">{t("Charge / Los-Nr.")}</span>
                            <Input value={l.batch} onChange={(e) => update(l.key, { batch: e.target.value })} placeholder="z. B. L2410-07" className="mt-0.5 w-40" aria-label={t("Charge {sku}", { sku: l.product.sku })} />
                          </label>
                        )}
                        {l.product.handling?.bestBeforeManaged && (
                          <label className="block">
                            <span className="text-[11.5px] font-medium text-fg-muted">{t("MHD (mindestens haltbar bis)")}</span>
                            <Input type="date" value={l.bestBefore} onChange={(e) => update(l.key, { bestBefore: e.target.value })} className="mt-0.5 w-44" aria-label={t("MHD {sku}", { sku: l.product.sku })} />
                          </label>
                        )}
                        <button type="button" onClick={() => splitLot(l.key)} className="h-8 inline-flex items-center gap-1 text-[12.5px] font-medium text-fg-muted hover:text-fg">
                          <Plus className="size-3.5" /> {t("weitere")} {l.product.handling?.batchManaged ? t("Charge") : t("MHD")}
                        </button>
                        <span className="basis-full text-[11.5px] text-fg-subtle">
                          {l.product.handling?.batchManaged && l.product.handling?.bestBeforeManaged ? t("Charge und MHD") : l.product.handling?.batchManaged ? t("Charge") : t("MHD")} {t("optional – geht als Anmerkung ans Lager. Mehrere Chargen/MHD? „weitere“ anklicken und Menge aufteilen.")}
                        </span>
                      </div>
                    )}
                    </li>
                  ))}
                </ul>
              ) : (
                <div className="mt-3 rounded-xl border border-dashed border-border py-8 text-center text-fg-muted">
                  <PackagePlus className="size-5 mx-auto mb-2 text-fg-subtle" />
                  {t("Artikel suchen oder eine Liste aus Excel einfügen")}
                </div>
              )}
            </div>
          </div>

          <div className="px-6 h-16 border-t border-border flex items-center gap-3">
            {blocked.length > 0 ? (
              <span className="text-[12.5px] text-danger">{blocked.length} {t("Artikel mit unvollständigen Stammdaten – bitte zuerst ergänzen")}</span>
            ) : (
              <span className="text-[12.5px] text-fg-muted tabular-nums">{lines.length} {lines.length === 1 ? t("Position") : t("Positionen")} · {fmtNumber(total)} {total === 1 ? t("Einheit") : t("Einheiten")}</span>
            )}
            <div className="ml-auto flex gap-2">
              <Dialog.Close asChild><Button variant="ghost">{t("Abbrechen")}</Button></Dialog.Close>
              <Button variant="primary" disabled={lines.length === 0 || blocked.length > 0} loading={create.isPending} onClick={() => create.mutate()}>{t("Ans Lager übermitteln")}</Button>
            </div>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
